/**
 * WeatherTerrainDisplay Component
 *
 * Displays the active weather or terrain effect at the top of battle screens.
 * Shows an icon, name, and remaining ticks for the effect.
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, CloudRain, Wind, Snowflake, Zap, Leaf, Brain, Sparkles, Mountain } from 'lucide-react';

/**
 * Configuration for weather/terrain effects
 */
const WEATHER_TERRAIN_CONFIG = {
  // Weather effects
  weather_sun: {
    name: 'Harsh Sunlight',
    icon: Sun,
    bgGradient: 'from-amber-400 to-orange-500',
    iconColor: 'text-yellow-300',
    textColor: 'text-white'
  },
  weather_rain: {
    name: 'Rain',
    icon: CloudRain,
    bgGradient: 'from-blue-400 to-blue-600',
    iconColor: 'text-blue-200',
    textColor: 'text-white'
  },
  weather_sand: {
    name: 'Sandstorm',
    icon: Wind,
    bgGradient: 'from-amber-600 to-yellow-700',
    iconColor: 'text-amber-200',
    textColor: 'text-white'
  },
  weather_hail: {
    name: 'Hail',
    icon: Snowflake,
    bgGradient: 'from-cyan-400 to-blue-500',
    iconColor: 'text-cyan-100',
    textColor: 'text-white'
  },
  // Terrain effects
  terrain_electric: {
    name: 'Electric Terrain',
    icon: Zap,
    bgGradient: 'from-yellow-400 to-amber-500',
    iconColor: 'text-yellow-200',
    textColor: 'text-gray-900'
  },
  terrain_grassy: {
    name: 'Grassy Terrain',
    icon: Leaf,
    bgGradient: 'from-green-400 to-emerald-600',
    iconColor: 'text-green-200',
    textColor: 'text-white'
  },
  terrain_psychic: {
    name: 'Psychic Terrain',
    icon: Brain,
    bgGradient: 'from-pink-400 to-purple-600',
    iconColor: 'text-pink-200',
    textColor: 'text-white'
  },
  terrain_misty: {
    name: 'Misty Terrain',
    icon: Sparkles,
    bgGradient: 'from-pink-300 to-purple-400',
    iconColor: 'text-pink-100',
    textColor: 'text-white'
  },
  terrain_earth: {
    name: 'Earth Terrain',
    icon: Mountain,
    bgGradient: 'from-amber-700 to-stone-600',
    iconColor: 'text-amber-200',
    textColor: 'text-white'
  }
};

/**
 * WeatherTerrainDisplay component
 * @param {Object} weather - Weather state object { type: string, ticksRemaining: number, caster: number }
 */
const WeatherTerrainDisplay = ({ weather }) => {
  // Don't render if no weather or terrain is active
  if (!weather?.type || !weather?.ticksRemaining || weather.ticksRemaining <= 0) {
    return null;
  }

  // Normalize weather type - backend sends 'sun' but we need 'weather_sun'
  // Terrain types are already prefixed correctly (terrain_electric, etc.)
  let weatherType = weather.type;
  if (!weatherType.startsWith('weather_') && !weatherType.startsWith('terrain_')) {
    weatherType = `weather_${weatherType}`;
  }

  const config = WEATHER_TERRAIN_CONFIG[weatherType];

  // If unknown weather/terrain type, don't render
  if (!config) {
    console.warn(`Unknown weather/terrain type: ${weather.type}`);
    return null;
  }

  const IconComponent = config.icon;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.9 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className={`
          bg-gradient-to-r ${config.bgGradient}
          rounded-xl px-4 py-2 shadow-lg
          flex items-center justify-center gap-3
          border border-white/20
        `}
      >
        {/* Animated icon */}
        <motion.div
          animate={{
            scale: [1, 1.1, 1],
            rotate: weather.type.includes('weather') ? [0, 5, -5, 0] : 0
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        >
          <IconComponent size={20} className={config.iconColor} />
        </motion.div>

        {/* Weather/Terrain name */}
        <span className={`font-bold text-sm ${config.textColor}`}>
          {config.name}
        </span>
      </motion.div>
    </AnimatePresence>
  );
};

export default WeatherTerrainDisplay;
